import type { BridgeSuit } from '@chessmate/game-engine';
import type { Edition } from '../edition/types.js';
import { type PackColour, type PackRank as FilePackRank, type PackSuit, resolvePackFile } from '@chessmate/pack-format';
import { bundledPack } from './card-pack-bundled.js';
import { ADULT_ONLY_PACKS } from './card-packs-adult.js';

declare const __EDITION__: string | undefined;

/**
 * Card packs: alternative art for Jack, Queen, King and Ace (docs/proposals/card-pack-marketplace.md).
 * The art lives in `public/packs/<id>/<rank>-<suit>.webp` and is generated from `scripts/packs/definitions.ts`
 * (keep the ids, names and editions below in step with it; `card-packs.test.ts` guards the ids).
 * `classic` is the built-in deck (the drawn bust plus the generated portraits in `public/cards/`) and is
 * always available, so a stored choice that is no longer offered falls back to it.
 */
export const CLASSIC_PACK = 'classic';

export interface CardPackInfo {
  readonly id: string;
  readonly name: string;
  readonly tagline: string;
  readonly family: 'fantasy' | 'manga' | 'community';
  /** Editions the pack may appear in. The spooky packs are adult-only until the operator decides otherwise. */
  readonly editions: readonly Edition[];
  /** The image files the pack has (`king-red.webp`, `king-hearts.webp`, ...); a card with none falls back to the classic art. */
  readonly files: readonly string[];
  /** Where the files are served from, no trailing slash: `/packs/<id>` for a bundled pack, the marketplace for a community one. */
  readonly base: string;
  /** Appended to every image URL (a community pack's `?edition=`, which the marketplace uses to keep mature art from the kids edition). */
  readonly query?: string;
  readonly author?: string;
}

const SHARED_PACKS: readonly CardPackInfo[] = [
  bundledPack({ id: 'paladin', name: 'Order of the Dawn', tagline: 'Plate, light and an oath kept.', family: 'fantasy', editions: ['kids', 'adult'] }),
  bundledPack({ id: 'elven', name: 'Silverleaf Court', tagline: 'Moonlight, mail and ancient bows.', family: 'fantasy', editions: ['kids', 'adult'] }),
  bundledPack({ id: 'samurai', name: 'Sakura Samurai', tagline: 'Blossoms, blades and a shogun’s court.', family: 'manga', editions: ['kids', 'adult'] }),
  bundledPack({ id: 'mecha', name: 'Starlight Mecha Academy', tagline: 'Pilots, captains and giant machines.', family: 'manga', editions: ['kids', 'adult'] }),
];

/** Every pack, for tests and tooling only. A function, so a child build that never calls it drops the adult-only packs. */
export function allCardPacks(): readonly CardPackInfo[] {
  return [...SHARED_PACKS, ...ADULT_ONLY_PACKS];
}

/** The packs this BUILD knows about: a child build holds none of the adult-only ones (they tree-shake out). */
export const CARD_PACKS: readonly CardPackInfo[] = typeof __EDITION__ !== 'undefined' && __EDITION__ === 'adult' ? [...SHARED_PACKS, ...ADULT_ONLY_PACKS] : SHARED_PACKS;

export function packsForEdition(editionId: Edition, pool: readonly CardPackInfo[] = CARD_PACKS): readonly CardPackInfo[] {
  return pool.filter((p) => p.editions.includes(editionId));
}

/** The pack to draw with: the stored id if this edition offers it, otherwise null (the classic deck). */
export function resolvePack(packId: string, editionId: Edition, pool: readonly CardPackInfo[] = CARD_PACKS): CardPackInfo | null {
  if (packId === CLASSIC_PACK) return null;
  return packsForEdition(editionId, pool).find((p) => p.id === packId) ?? null;
}

const RANK_NAME: Record<11 | 12 | 13 | 14 | 15, FilePackRank> = { 11: 'jack', 12: 'queen', 13: 'king', 14: 'ace', 15: 'joker' };
const SUIT_NAME: Record<BridgeSuit, PackSuit> = { S: 'spades', H: 'hearts', D: 'diamonds', C: 'clubs' };

export type PackRank = keyof typeof RANK_NAME;
export type { PackColour };

/**
 * The URL of the pack's picture for this card, or null when the pack has none (the card then keeps its classic art).
 * A per-suit file wins over the red/black one; see `resolvePackFile` in @chessmate/pack-format, which the marketplace uses too.
 */
export function packCardUrl(pack: CardPackInfo, rank: PackRank, suit: BridgeSuit): string | null {
  const file = resolvePackFile((name) => pack.files.includes(name), RANK_NAME[rank], SUIT_NAME[suit]);
  return file ? `${pack.base}/${file}${pack.query ?? ''}` : null;
}
